import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ViewChild, AfterViewInit, Component, QueryList, ViewChildren, OnInit } from '@angular/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserService } from 'src/app/shared/services/user-services/user.service';

export interface AttendanceEntry {
  id: number;
  user_type: string;
  user_id: number;
  date: string;
  status: string;
  latitude: string;
  longitude: string;
  location: string;
  intime: string;
  outtime: string;
  remark: string;
  device_id: string;
  device_type: string;
  project_id: number;
  working_hours: string;
  intimeimage: string;
  outtimeimage: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  full_name: string;
  user_email: string;
  project_name: string;
  org_name: string;
  approval_status?: string;
  approved_by?: number;
  approved_at?: string;
  adjusted_hours?: number;
  supervisor_remark?: string;
}

export interface ProjectAttendance {
  project_id: number;
  project_name: string;
  total_employees: number;
  total_hours: number;
  avg_hours: number;
  attendance_records: AttendanceEntry[];
  date_range?: { start: string; end: string };
}

export interface EmployeeSummary {
  user_id: number;
  full_name: string;
  total_days: number;
  total_hours: number;
  avg_hours_per_day: number;
  on_time_count: number;
  late_count: number;
  absent_count: number;
}

export interface SupervisorReport {
  employeeName: string;
  employeeId: number;
  date: string;
  timeIn: string;
  timeOut: string;
  location: string;
  hoursWorked: number;
  adjustedHours: number;
  totalHours: number;
  remarks: string;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approvedBy: string;
  attendanceId: number;
}

@Component({
  selector: 'app-attendance-list',
  templateUrl: './attendance-list.component.html',
  styleUrls: ['./attendance-list.component.css']
})
export class AttendanceListComponent implements OnInit, AfterViewInit {
  // Current view type
  currentView: 'employee' | 'supervisor' | 'project' = 'employee';
  
  // User info
  userRole: string = '';
  userId: number = 0;
  orgId: number = 0;
  isSupervisor: boolean = false;
  isAdmin: boolean = false;
  
  // Filter options
  projects: any[] = [];
  employees: any[] = [];
  selectedProjectId: number | null = null;
  selectedEmployeeId: number | null = null;
  selectedStatus: string = '';
 range: FormGroup;
  searchKeyword: string = '';

  // Employee View
  employeeDisplayedColumns: string[] = ['date', 'timeIn', 'timeOut', 'location', 'hoursWorked', 'status', 'remarks'];
  employeeDataSource = new MatTableDataSource<AttendanceEntry>([]);
  employeeSummary: EmployeeSummary | null = null;

  // Supervisor View
  supervisorDisplayedColumns: string[] = ['employeeName', 'date', 'timeIn', 'timeOut', 'location', 'hoursWorked', 'adjustedHours', 'totalHours', 'approvalStatus', 'actions'];
  supervisorDataSource = new MatTableDataSource<SupervisorReport>([]);
  pendingApprovalsCount: number = 0;

  // Project View
  projectDisplayedColumns: string[] = ['fullName', 'date', 'timeIn', 'timeOut', 'location', 'hoursWorked', 'status'];
  projectDataSource = new MatTableDataSource<AttendanceEntry>([]);
  projectSummary: ProjectAttendance | null = null;
  projectStats: { totalEmployees: number; totalHours: number; avgHours: number } = {
    totalEmployees: 0,
    totalHours: 0,
    avgHours: 0
  };

  // Loading states
  isLoading: boolean = false;

  @ViewChildren(MatPaginator) paginators!: QueryList<MatPaginator>;
  @ViewChildren(MatSort) sorts!: QueryList<MatSort>;

  constructor(
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private fb: FormBuilder,
    private userService: UserService
  ) {
      this.range = this.fb.group({
      start: [null],
      end: [null],
    });
  }

   ngOnInit(): void {
    this.initializeUserInfo();
    this.loadFiltersData();
    this.setDefaultView();
    this.fetchDataForCurrentView();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupPaginatorsAndSorts();
    });
  }

  private initializeUserInfo(): void {
    const sessionData = this.userService.getUserDetailsFromSession();
    // Handle nested structure: sessionData could be { user: {...} } or just the user object
    const userData = sessionData?.user || sessionData;
    
    if (userData) {
      this.userId = userData.id || userData.user_id || userData.staff_id || 0;
      this.orgId = userData.org_id || 0;
      this.userRole = userData.role_name || userData.role || '';
      
      // Determine if user is supervisor (has team members)
      this.isSupervisor = ['Admin', 'Organization', 'Supervisor', 'Manager'].includes(this.userRole);
      this.isAdmin = ['Admin', 'Organization'].includes(this.userRole);
    }
    
    console.log('User Info Initialized:', { userId: this.userId, orgId: this.orgId, userRole: this.userRole });
  }

  private setDefaultView(): void {
    if (this.isAdmin) {
      this.currentView = 'supervisor';
    } else if (this.isSupervisor) {
      this.currentView = 'supervisor';
    } else {
      this.currentView = 'employee';
    }
  }

  private loadFiltersData(): void {
    // Load projects for filter
    this.apiService.getProject({}).subscribe({
      next: (res: any) => {
        this.projects = res.data || res || [];
      },
      error: (err: any) => console.error('Error loading projects:', err)
    });

    // Load employees for supervisor view
    if (this.isSupervisor || this.isAdmin) {
      this.apiService.getStaff({ keyword: '' }).subscribe({
        next: (res: any) => {
          this.employees = res.data || res || [];
        },
        error: (err: any) => console.error('Error loading employees:', err)
      });
    }
  }

  private setupPaginatorsAndSorts(): void {
    const paginatorArray = this.paginators?.toArray() || [];
    const sortArray = this.sorts?.toArray() || [];

    if (paginatorArray[0]) {
      this.employeeDataSource.paginator = paginatorArray[0];
    }
    if (sortArray[0]) {
      this.employeeDataSource.sort = sortArray[0];
    }

    if (paginatorArray[1]) {
      this.supervisorDataSource.paginator = paginatorArray[1];
    }
    if (sortArray[1]) {
      this.supervisorDataSource.sort = sortArray[1];
    }

    if (paginatorArray[2]) {
      this.projectDataSource.paginator = paginatorArray[2];
    }
    if (sortArray[2]) {
      this.projectDataSource.sort = sortArray[2];
    }
  }

  // ========== VIEW SWITCHING ==========
  switchView(view: 'employee' | 'supervisor' | 'project'): void {
    this.currentView = view;
    this.fetchDataForCurrentView();
  }

  // ========== DATA FETCHING ==========
  fetchDataForCurrentView(): void {
    this.isLoading = true;
    
    switch (this.currentView) {
      case 'employee':
        this.fetchEmployeeAttendance();
        break;
      case 'supervisor':
        this.fetchSupervisorData();
        break;
      case 'project':
        this.fetchProjectAttendance();
        break;
    }
  }

  // Employee View - Own attendance
  private fetchEmployeeAttendance(): void {
    if (!this.userId) {
      console.warn('User ID not available for fetching attendance');
      this.employeeDataSource.data = [];
      this.employeeSummary = null;
      this.isLoading = false;
      return;
    }

    const params = this.buildFilterParams();
    params.userId = this.userId.toString();

    this.apiService.getAttendance(params, 'staff').subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : (res.data || []);
        this.employeeDataSource.data = data;
        this.calculateEmployeeSummary(data);
        this.isLoading = false;
      },
      error: (err) => {

        console.error('Error fetching employee attendance:', err);
        this.toastrMsgService.error('No attendance data found');
        this.isLoading = false;
      }
    });
  }

  // Supervisor View - Team attendance with approval features
  private fetchSupervisorData(): void {
    const params = this.buildFilterParams();
    
    this.apiService.getAttendance(params, 'all').subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : (res.data || []);
        this.supervisorDataSource.data = this.transformToSupervisorReport(data);
        this.pendingApprovalsCount = this.supervisorDataSource.data.filter(
          r => r.approvalStatus === 'pending'
        ).length;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching supervisor data:', err);
        this.toastrMsgService.error('No team attendance data found');
        this.isLoading = false;
      }
    });
  }

  // Project View - Attendance grouped by project
  private fetchProjectAttendance(): void {
    const params = this.buildFilterParams();
    
    if (this.selectedProjectId) {
      params.projectId = this.selectedProjectId.toString();
    }

    this.apiService.getAttendance(params, 'all').subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : (res.data || []);
        this.projectDataSource.data = data;
        this.calculateProjectStats(data);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching project attendance:', err);
        this.toastrMsgService.error('No project attendance data found');
        this.isLoading = false;
      }
    });
  }

  private buildFilterParams(): any {
    const params: any = {
      userId: '',
      projectId: '',
      startDate: '',
      endDate: ''
    };

    if (this.selectedProjectId) {
      params.projectId = this.selectedProjectId.toString();
    }

    if (this.selectedEmployeeId) {
      params.userId = this.selectedEmployeeId.toString();
    }

    if (this.range.value.start) {
      params.startDate = this.formatDate(this.range.value.start);
    }

    if (this.range.value.end) {
      params.endDate = this.formatDate(this.range.value.end);
    }

    return params;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // ========== DATA TRANSFORMATIONS ==========
  private transformToSupervisorReport(data: AttendanceEntry[]): SupervisorReport[] {
    return data.map(entry => ({
      employeeName: entry.full_name || entry.user_name || 'Unknown',
      employeeId: entry.user_id,
      date: entry.date,
      timeIn: entry.intime || '-',
      timeOut: entry.outtime || '-',
      location: entry.location || '-',
      hoursWorked: parseFloat(entry.working_hours) || 0,
      adjustedHours: entry.adjusted_hours || 0,
      totalHours: (parseFloat(entry.working_hours) || 0) + (entry.adjusted_hours || 0),
      remarks: entry.remark || '',
      approvalStatus: (entry.approval_status as 'pending' | 'approved' | 'rejected') || 'pending',
      approvedBy: entry.approved_by ? `ID: ${entry.approved_by}` : '-',
      attendanceId: entry.id
    }));
  }

  private calculateEmployeeSummary(data: AttendanceEntry[]): void {
    if (data.length === 0) {
      this.employeeSummary = null;
      return;
    }

    const totalHours = data.reduce((sum, entry) => sum + (parseFloat(entry.working_hours) || 0), 0);
    const onTimeCount = data.filter(entry => {
      if (!entry.intime) return false;
      const [hours] = entry.intime.split(':').map(Number);
      return hours <= 9; // Assuming 9 AM is on-time
    }).length;

    this.employeeSummary = {
      user_id: this.userId,
      full_name: data[0]?.full_name || data[0]?.user_name || 'You',
      total_days: data.length,
      total_hours: totalHours,
      avg_hours_per_day: data.length > 0 ? totalHours / data.length : 0,
      on_time_count: onTimeCount,
      late_count: data.length - onTimeCount,
      absent_count: 0 // Would need separate logic for this
    };
  }

  private calculateProjectStats(data: AttendanceEntry[]): void {
    const uniqueEmployees = new Set(data.map(entry => entry.user_id));
    const totalHours = data.reduce((sum, entry) => sum + (parseFloat(entry.working_hours) || 0), 0);

    this.projectStats = {
      totalEmployees: uniqueEmployees.size,
      totalHours: totalHours,
      avgHours: uniqueEmployees.size > 0 ? totalHours / uniqueEmployees.size : 0
    };
  }

  // ========== FILTER FUNCTIONS ==========
  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    
    switch (this.currentView) {
      case 'employee':
        this.employeeDataSource.filter = filterValue;
        break;
      case 'supervisor':
        this.supervisorDataSource.filter = filterValue;
        break;
      case 'project':
        this.projectDataSource.filter = filterValue;
        break;
    }
  }

  onProjectChange(): void {
    if (this.currentView === 'project') {
      this.fetchProjectAttendance();
    } else {
      this.fetchDataForCurrentView();
    }
  }

  onEmployeeChange(): void {
    this.fetchDataForCurrentView();
  }

  onDateRangeChange(): void {
    if (this.range.value.start && this.range.value.end) {
      this.fetchDataForCurrentView();
    }
  }

  clearFilters(): void {
    this.selectedProjectId = null;
    this.selectedEmployeeId = null;
    this.selectedStatus = '';
    this.range.reset();
    this.fetchDataForCurrentView();
  }

  // ========== SUPERVISOR ACTIONS ==========
  adjustHours(record: SupervisorReport, adjustment: number): void {
    record.adjustedHours = adjustment;
    record.totalHours = record.hoursWorked + adjustment;
  }

  approveAttendance(record: SupervisorReport): void {
    const payload = {
      approval_status: 'approved',
      adjusted_hours: record.adjustedHours,
      approved_by: this.userId,
      supervisor_remark: record.remarks
    };

    this.apiService.updateAttendance(record.attendanceId, payload).subscribe({
      next: () => {
        record.approvalStatus = 'approved';
        this.pendingApprovalsCount--;
        this.toastrMsgService.success('Attendance approved successfully');
      },
      error: (err: any) => {
        console.error('Error approving attendance:', err);
        this.toastrMsgService.error('Failed to approve attendance, please try again');
      }
    });
  }

  rejectAttendance(record: SupervisorReport, reason: string = ''): void {
    const payload = {
      approval_status: 'rejected',
      supervisor_remark: reason || 'Rejected by supervisor',
      approved_by: this.userId
    };

    this.apiService.updateAttendance(record.attendanceId, payload).subscribe({
      next: () => {
        record.approvalStatus = 'rejected';
        this.pendingApprovalsCount--;
        this.toastrMsgService.success('Attendance rejected successfully');
      },
      error: (err: any) => {
        console.error('Error rejecting attendance:', err);
        this.toastrMsgService.error('Failed to reject attendance, please try again');
      }
    });
  }

  bulkApprove(): void {
    const pendingRecords = this.supervisorDataSource.data.filter(r => r.approvalStatus === 'pending');
    
    if (pendingRecords.length === 0) {
      this.toastrMsgService.error('No pending records to approve');
      return;
    }

    const confirmApprove = confirm(`Are you sure you want to approve ${pendingRecords.length} attendance records?`);
    if (!confirmApprove) return;

    pendingRecords.forEach(record => {
      this.approveAttendance(record);
    });
  }

  // ========== UTILITY FUNCTIONS ==========
  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'present':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'absent':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'leave':
        return 'bg-blue-100 text-blue-800';
      case 'late':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getApprovalStatusIcon(status: string): string {
    switch (status) {
      case 'approved':
        return 'check_circle';
      case 'rejected':
        return 'cancel';
      case 'pending':
        return 'schedule';
      default:
        return 'help_outline';
    }
  }

  formatTime(time: string): string {
    if (!time || time === '-') return '-';
    return time.substring(0, 5); // HH:mm format
  }

  formatHours(hours: number | string): string {
    const numHours = typeof hours === 'string' ? parseFloat(hours) : hours;
    if (isNaN(numHours)) return '-';
    return numHours.toFixed(1) + 'h';
  }

  // ========== EXPORT FUNCTIONS ==========
  exportToCSV(): void {
    let data: any[];
    let filename: string;

    switch (this.currentView) {
      case 'employee':
        data = this.employeeDataSource.filteredData;
        filename = 'my-attendance.csv';
        break;
      case 'supervisor':
        data = this.supervisorDataSource.filteredData;
        filename = 'team-attendance.csv';
        break;
      case 'project':
        data = this.projectDataSource.filteredData;
        filename = 'project-attendance.csv';
        break;
      default:
        data = [];
        filename = 'attendance.csv';
    }

    if (data.length === 0) {
      this.toastrMsgService.error('No data to export');
      return;
    }

    const csvData = this.convertToCSV(data);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    window.URL.revokeObjectURL(url);
  }

  private convertToCSV(data: any[]): string {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const rows = data.map(row => 
      headers.map(field => `"${row[field] ?? ''}"`).join(',')
    );
    return [headers.join(','), ...rows].join('\r\n');
  }

  // ========== VIEW PERMISSIONS ==========
  canAccessSupervisorView(): boolean {
    return this.isSupervisor || this.isAdmin;
  }

  canAccessProjectView(): boolean {
    return this.isSupervisor || this.isAdmin;
  }

  canApproveAttendance(): boolean {
    return this.isSupervisor || this.isAdmin;
  }

  // Get selected project name for display
  getSelectedProjectName(): string {
    if (!this.selectedProjectId || !this.projects.length) {
      return 'All';
    }
    const project = this.projects.find(p => p.id === this.selectedProjectId);
    return project?.project_name || 'All';
  }
}
